<template>
  <section class="s-strip s-strip--short s-strip--theme-grey">
    <div class="s-strip__header mb-40">
      <div class="page-container">
        <div class="b-strip-header align-items-center">
          <div class="title h2">{{ title }}</div>
        </div>
      </div>
    </div>

    <div class="page-container">
      <BookingWizard :step="step" />
    </div>
  </section>

  <section class="s-strip">
    <div class="page-container">
      <div ref="strip" class="s-booking-layout">
        <Loader v-if="isLoading"></Loader>
        <Alert
          v-if="errorMsgs.length"
          type="danger"
          :messages="errorMsgs"></Alert>

        <div class="mb-30 h1">
          <span class="light">
            {{ fullName }}
            <span v-if="referenceNumber">|</span>
            {{ referenceNumber }}
          </span>
        </div>

        <div class="d-block d-lg-none mb-40">
          <BookingAsideAccordion
            v-if="vehicle"
            :item="vehicle"
            :payment="payment">
            <BookingAside :item="vehicle" :payment="payment" />
          </BookingAsideAccordion>
        </div>

        <div class="s-booking-layout__body">
          <div v-show="remoteUrl" class="s-booking-layout__main">
            <iframe id="payment-iframe-frame"></iframe>
          </div>

          <div v-show="!remoteUrl" class="s-booking-layout__main">
            <div
              class="d-none d-lg-flex justify-content-between align-items-center gx-2 mb-30">
              <div class="fs-20">
                <span class="extrabold">
                  {{ dictionary.yourReservation }}
                </span>
              </div>
            </div>

            <BookingYourReservation
              v-if="pickupDetails && returnDetails"
              :pickup-details="pickupDetails"
              :return-details="returnDetails"
              class="d-none d-lg-flex" />

            <Accordion
              :title="dictionary.yourReservation"
              class="d-block d-lg-none mb-40">
              <BookingYourReservation
                v-if="pickupDetails && returnDetails"
                :pickup-details="pickupDetails"
                :return-details="returnDetails" />
            </Accordion>

            <div class="row d-none d-lg-flex">
              <div class="col mb-30 fs-20">
                <span class="extrabold">
                  {{ dictionary.personalInformation }}
                </span>
              </div>
            </div>

            <BookingPersonalInfo
              v-if="reservationPersonalInfo"
              :personal-info="reservationPersonalInfo"
              class="d-none d-lg-flex" />

            <Accordion
              :title="dictionary.personalInformation"
              class="d-block d-lg-none mb-40">
              <BookingPersonalInfo
                v-if="reservationPersonalInfo"
                :personal-info="reservationPersonalInfo" />
            </Accordion>

            <MilesNCash
              v-if="
                vehicle &&
                showMilesNBonus &&
                shouldDisplayPaymentForm &&
                payment.IsPrepaid
              "
              :total-cost="payment.TotalCostDecimal"
              @has-balance-due="changeBalanceDueStatus" />

            <div v-if="shouldDisplayPaymentForm">
              <div class="row">
                <div class="col mb-30 fs-20">
                  <span class="extrabold">
                    {{ dictionary.creditCardHeader }}
                  </span>
                </div>
              </div>

              <div v-if="registeredCardForm">
                <div v-if="!showCreditCardForm">
                  <form
                    autocomplete="off"
                    @submit.prevent="onSubmitRegisteredCard">
                    <div class="row">
                      <div class="col mb-30">
                        <div class="b-creditcard-change">
                          <div class="b-creditcard-change__body">
                            <div>
                              <span>
                                {{ dictionary.changeCardPrompt }}
                              </span>
                            </div>
                            <div>
                              <button
                                type="button"
                                class="btn btn-primary"
                                @click="onClickChangeCard">
                                {{ dictionary.buttonChangeCardText }}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="row">
                      <div class="col mb-30">
                        <BookingLegalTerms :records="legalTerms" />
                      </div>
                    </div>

                    <div class="row">
                      <div class="col mb-30 d-flex justify-content-center">
                        <button type="submit" class="btn btn-primary">
                          {{ dictionary.buttonSubmitText }}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>

              <div v-if="creditCardForm">
                <div v-if="showCreditCardForm">
                  <div v-if="nonPrepaidExplanatoryMessage" class="row">
                    <div class="col mb-30 semibold">
                      <div class="credit-card-info">
                        <div><IdeaLottie width="20" /></div>
                        <div v-html="nonPrepaidExplanatoryMessage"></div>
                      </div>
                    </div>
                  </div>

                  <form autocomplete="off" @submit.prevent="onSubmitCreditCard">
                    <BookingCreditCard
                      @change-credit-card-data="onChangeCreditCardData" />

                    <div class="row">
                      <div class="col mb-30">
                        <BookingLegalTerms :records="legalTerms" />
                      </div>
                    </div>

                    <div class="row">
                      <div class="col mb-30 d-flex justify-content-center">
                        <button type="submit" class="btn btn-primary">
                          {{ dictionary.buttonSubmitText }}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
            <div v-else>
              <form autocomplete="off" @submit.prevent="onSubmitConfirm">
                <div class="row">
                  <div class="col mb-30">
                    <BookingLegalTerms :records="legalTerms" />
                  </div>
                </div>

                <div class="row">
                  <div class="col mb-30 d-flex justify-content-center">
                    <button type="submit" class="btn btn-primary">
                      {{ dictionary.buttonSubmitText }}
                    </button>
                  </div>
                </div>
              </form>
            </div>

            <div class="row">
              <div
                class="col d-flex justify-content-center"
                v-html="helpText"></div>
            </div>
          </div>

          <div v-if="vehicle" class="s-booking-layout__aside d-none d-lg-block">
            <BookingAside
              :item="vehicle"
              :payment="payment"
              :redeem-miles-enabled="redeemMilesEnabled"
              :miles-data="milesData" />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import jQuery from 'jquery'
import axios from 'axios'
import useVuelidate from '@vuelidate/core'
import * as lib from '../../lib/lib'
import Accordion from '../components/common/Accordion.vue'
import BookingWizard from '../components/BookingWizard.vue'
import BookingAside from '../components/BookingAside.vue'
import BookingAsideAccordion from '../components/BookingAsideAccordion.vue'
import BookingPersonalInfo from '../components/BookingPersonalInfo.vue'
import BookingYourReservation from '../components/BookingYourReservation.vue'
import BookingCreditCard from '../components/BookingCreditCard.vue'
import BookingLegalTerms from '../components/BookingLegalTerms.vue'
import Loader from '../components/common/Loader.vue'
import Alert from '../components/common/Alert.vue'
import bookingMixins from '../mixins/booking-mixins'
import IdeaLottie from './../components/lotties/Idea.vue'
import MilesNCash from './../components/MilesNCash.vue'

export default {
  components: {
    Loader,
    Alert,
    BookingWizard,
    BookingAside,
    BookingAsideAccordion,
    Accordion,
    BookingPersonalInfo,
    BookingYourReservation,
    BookingCreditCard,
    BookingLegalTerms,
    IdeaLottie,
    MilesNCash,
  },
  mixins: [bookingMixins],
  setup() {
    return { v$: useVuelidate() }
  },
  data() {
    return {
      scrollOffsetY: 125,
      isLoading: false,
      errorMsgs: [],
      // always show last step as active
      token: null,
      step: 2,
      title: null,
      vehicle: null,
      payment: null,
      reservationIdentityInfo: null,
      reservationPersonalInfo: null,
      pickupDetails: null,
      returnDetails: null,
      fullName: '',
      referenceNumber: '',
      legalTerms: [],
      creditCardData: {
        cardNumber: '',
        cardHolder: '',
        cardExpirationMonth: '',
        cardExpirationYear: '',
        cardSecureCode: '',
      },
      remoteUrl: null,
      shouldDisplayPaymentForm: false,
      registeredCardForm: null,
      creditCardForm: null,
      helpText: '',
      nonPrepaidExplanatoryMessage: '',
      showCreditCardForm: false,

      // Miles and Bonus
      showMilesNBonus: lib.getData('milesNBonusData'),
      milesNBonusLoggedIn: false,
      redeemMilesEnabled: false,
      milesData: null,
    }
  },

  computed: {
    hasBalanceDue() {
      if (!this.redeemMilesEnabled) return true

      return this.milesData.remainingAmount > 0
    },
  },

  mounted() {
    const {
      token,
      title,
      vehicle,
      Payment,
      reservationIdentityInfo,
      reservationPersonalInfo,
      reservationDetails,
      fullName,
      referenceNumber,
      legalTerms,
      shouldDisplayPaymentForm,
      registeredCardForm,
      creditCardForm,
      helpText,
      nonPrepaidExplanatoryMessage,
    } = lib.getData('bookingData')

    this.token = token
    this.title = title
    this.vehicle = vehicle
    this.payment = Payment
    this.reservationIdentityInfo = reservationIdentityInfo
    this.reservationPersonalInfo = reservationPersonalInfo
    this.pickupDetails = reservationDetails.pickupDetails
    this.returnDetails = reservationDetails.returnDetails
    this.fullName = fullName
    this.referenceNumber = referenceNumber
    this.legalTerms = legalTerms
    this.shouldDisplayPaymentForm = shouldDisplayPaymentForm
    this.registeredCardForm = registeredCardForm
    this.creditCardForm = creditCardForm
    this.helpText = helpText
    this.nonPrepaidExplanatoryMessage = nonPrepaidExplanatoryMessage

    if (lib.getData('milesNBonusData')) {
      const { configuration } = lib.getData('milesNBonusData')
      this.milesNBonusLoggedIn = configuration.milesNBonusLoggedIn

      if (!configuration.destinationAllowed) {
        this.showMilesNBonus = false
      }
    }

    this.setNavigatorValues()

    document.addEventListener('startNotifyTimer', (e) => {
      this.startNotifyTimer(e)
    })

    document.addEventListener('paymentRemoveIframe', (e) => {
      this.paymentRemoveIframe(e)
    })

    this.showCreditCardForm =
      this.shouldDisplayPaymentForm &&
      this.creditCardForm &&
      !this.registeredCardForm

    document.addEventListener('requestOtp', () => this.onRequestOtp())
    document.addEventListener('validateOtp', (e) =>
      this.onValidateOtp(e.detail)
    )
  },
  methods: {
    changeBalanceDueStatus(redeemMilesEnabled, milesData) {
      this.redeemMilesEnabled = redeemMilesEnabled
      this.milesData = milesData
    },

    paymentShowIframe() {
      console.log('paymentShowIframe')
      jQuery('#payment-iframe-frame').show()
      jQuery('#payment-iframe-frame').attr('src', this.remoteUrl)

      this.$nextTick(() => {
        lib.scrollToElem(jQuery(this.$refs.strip), 100)
        this.isLoading = false
      })
    },

    startNotifyTimer(e) {
      console.log('startNotifyTimer', e)

      this.isLoading = true

      const { checkUrl, iframeUrl } = e.detail

      let start = 0
      const timer = window.setInterval(() => {
        console.log('interval', start)

        if (start > 10) {
          console.log('start larger than 10')
          window.clearInterval(timer)
          jQuery('#payment-iframe-frame').attr('src', iframeUrl)
          return
        }
        axios
          .get(checkUrl)
          .then((resp) => {
            const { success, notified } = resp.data

            if (success && notified) {
              console.log('success && notified')
              window.clearInterval(timer)
              jQuery('#payment-iframe-frame').attr('src', iframeUrl)
              setTimeout(() => {
                this.isLoading = false
              }, 1000)
            }
          })
          .then(() => {
            start += 1
          })
      }, 1000)
    },

    paymentRemoveIframe(e) {
      console.log('paymentRemoveIframe', e)

      this.isLoading = true

      jQuery('#payment-iframe-frame').hide()

      const { errors, url } = e.detail
      if (url) {
        window.location.assign(url)
        return false
      }

      this.remoteUrl = ''
      this.isLoading = false
      this.errorMsgs = [this.dictionary.defaultErrorMessage]
      // if errors array is empty or
      // has just one item with empty string
      if (errors.length && lib.hasValue(errors[0])) {
        this.errorMsgs = errors
      }

      lib.scrollToElem(jQuery(this.$refs.strip), 100)
    },

    onChangeCreditCardData(data) {
      this.creditCardData = data
    },

    appendNavigatorData(formData) {
      formData.append('NavigatorLanguage', this.navigatorLanguage)
      formData.append('NavigatorColors', this.navigatorColors)
      formData.append('NavigatorHeight', this.navigatorHeight)
      formData.append('NavigatorWidth', this.navigatorWidth)
      formData.append('NavigatorTimezone', this.navigatorTimezone)
      formData.append('IsJavaEnabled', this.isJavaEnabled)
      formData.append('ChallengeWindowSize', this.challengeWindowSize)
    },

    appendNavigatorDataCreditCard(formData) {
      formData.append('cNavigatorLanguage', this.navigatorLanguage)
      formData.append('cNavigatorColors', this.navigatorColors)
      formData.append('cNavigatorHeight', this.navigatorHeight)
      formData.append('cNavigatorWidth', this.navigatorWidth)
      formData.append('cNavigatorTimezone', this.navigatorTimezone)
      formData.append('cIsJavaEnabled', this.isJavaEnabled)
      formData.append('cChallengeWindowSize', this.challengeWindowSize)
    },

    appendCreditCardData(formData) {
      formData.append('CardNumber', this.creditCardData.cardNumber)
      formData.append('CardHolder', this.creditCardData.cardHolder)
      // prettier-ignore
      formData.append('CardExpirationMonth', this.creditCardData.cardExpirationMonth)
      // prettier-ignore
      formData.append('CardExpirationYear', this.creditCardData.cardExpirationYear)
      formData.append('CardSecureCode', this.creditCardData.cardSecureCode)
    },

    onRequestOtp() {
      if (this.isLoading) {
        return false
      }

      this.isLoading = true

      document.dispatchEvent(
        new CustomEvent('milesNBonusLoading', {
          detail: { loading: this.isLoading },
        })
      )

      const formData = new FormData()
      formData.append('ConfirmationAcceptTerms', true)
      formData.append('Token', this.token)
      formData.append('UseMilesRedemption', this.redeemMilesEnabled)
      formData.append('RedeemedMiles', this.milesData.redeemedMiles)
      formData.append('ReferenceNumber', this.referenceNumber)

      if (this.showCreditCardForm) {
        this.appendNavigatorDataCreditCard(formData)
      }
      this.appendNavigatorData(formData)

      const endpointSubmitData = this.showCreditCardForm
        ? this.configuration.enpointCreditCard
        : this.configuration.endpointRegisteredCard

      axios
        .post(endpointSubmitData, formData)
        .then((response) => {
          const { HasError, Errors } = response.data
          // response has error
          if (HasError) {
            this.handleError(Errors)
            return false
          }

          document.dispatchEvent(
            new CustomEvent('wrongOTP', { detail: { wrongCode: false } })
          )

          this.handleSuccess()
        })
        .catch((errors) => {
          this.handleError([this.dictionary.defaultErrorMessage])
          console.error(errors)
        })
    },

    onValidateOtp(data) {
      if (this.isLoading) {
        return false
      }

      this.isLoading = true

      document.dispatchEvent(
        new CustomEvent('milesNBonusLoading', {
          detail: { loading: this.isLoading },
        })
      )

      const formData = new FormData()
      formData.append('ConfirmationAcceptTerms', true)
      formData.append('Token', this.token)
      formData.append('UseMilesRedemption', this.redeemMilesEnabled)
      formData.append('RedeemedMiles', this.milesData.redeemedMiles)
      formData.append('Otp', data.otpCode)
      formData.append('ReferenceNumber', this.referenceNumber)

      if (this.showCreditCardForm) {
        this.appendNavigatorDataCreditCard(formData)
      }
      this.appendNavigatorData(formData)

      axios
        .post(this.configuration.endpointValidateOtp, formData)
        .then((response) => {
          const { HasError, Errors, Url, RemoteUrl } = response.data
          // response has error
          if (HasError) {
            this.handleError(Errors)
            document.dispatchEvent(
              new CustomEvent('wrongOTP', { detail: { wrongCode: true } })
            )
            return false
          }

          document.dispatchEvent(new CustomEvent('hideMilesNBonusOTP'))
          if (Url) {
            window.location.assign(Url)
          }
          if (RemoteUrl) {
            this.remoteUrl = RemoteUrl
            this.paymentShowIframe()
          }
          this.errorMsgs = []

          this.handleSuccess()
        })
        .catch((errors) => {
          this.handleError([this.dictionary.defaultErrorMessage])
          console.error(errors)
        })
    },

    onSubmitCreditCard() {
      this.v$.$validate()

      if (this.v$.$invalid) {
        this.$nextTick(() => {
          lib.scrollToValidation(this.scrollOffsetY)
        })
        return false
      }

      this.isLoading = true

      const formData = new FormData()

      formData.append('CreditCardAcceptTerms', true)
      formData.append('Token', this.token)
      formData.append('UseMilesRedemption', this.redeemMilesEnabled)

      if (this.redeemMilesEnabled) {
        formData.append('RedeemedMiles', this.milesData.redeemedMiles)
      }

      this.appendCreditCardData(formData)
      this.appendNavigatorDataCreditCard(formData)

      axios
        .post(this.configuration.enpointCreditCard, formData)
        .then((response) => {
          const { HasError, Errors, Url, RemoteUrl } = response.data
          // response has error
          if (HasError) {
            this.handleError(Errors)
            return false
          }

          if (this.redeemMilesEnabled) {
            document.dispatchEvent(new CustomEvent('showMilesNBonusOTP'))
            this.handleSuccess()
          } else {
            if (Url) {
              window.location.assign(Url)
            }
            if (RemoteUrl) {
              this.remoteUrl = RemoteUrl
              this.paymentShowIframe()
            }

            this.errorMsgs = []
          }
        })
        .catch((errors) => {
          this.handleError([this.dictionary.defaultErrorMessage])
          console.error(errors)
        })
    },

    onSubmitRegisteredCard() {
      this.v$.$validate()

      if (this.v$.$invalid) {
        this.$nextTick(() => {
          lib.scrollToValidation(this.scrollOffsetY)
        })
        return false
      }

      this.isLoading = true

      const formData = new FormData()
      formData.append('RegisteredCardAcceptTerms', true)
      formData.append('Token', this.token)
      formData.append('UseMilesRedemption', this.redeemMilesEnabled)

      if (this.redeemMilesEnabled) {
        formData.append('RedeemedMiles', this.milesData.redeemedMiles)
      }

      this.appendNavigatorData(formData)

      axios
        .post(this.configuration.endpointRegisteredCard, formData)
        .then((response) => {
          const { HasError, Errors, Url, RemoteUrl } = response.data
          // response has error
          if (HasError) {
            this.handleError(Errors)
            return false
          }

          if (this.redeemMilesEnabled) {
            document.dispatchEvent(new CustomEvent('showMilesNBonusOTP'))
            this.handleSuccess()
          } else {
            if (Url) {
              window.location.assign(Url)
            }
            if (RemoteUrl) {
              this.remoteUrl = RemoteUrl
              this.paymentShowIframe()
            }

            this.errorMsgs = []
          }
        })
        .catch((errors) => {
          this.handleError([this.dictionary.defaultErrorMessage])
          console.error(errors)
        })
    },

    onSubmitConfirm() {
      this.v$.$validate()

      if (this.v$.$invalid) {
        this.$nextTick(() => {
          lib.scrollToValidation(this.scrollOffsetY)
        })
        return false
      }

      this.isLoading = true

      const formData = new FormData()
      formData.append('ConfirmationAcceptTerms', true)
      formData.append('Token', this.token)

      axios
        .post(this.configuration.endpointConfirm, formData)
        .then((response) => {
          const { HasError, Errors, Url } = response.data
          // response has error
          if (HasError) {
            this.handleError(Errors)
            return false
          }

          if (Url) {
            window.location.assign(Url)
          }

          this.errorMsgs = []
        })
        .catch((errors) => {
          this.handleError([this.dictionary.defaultErrorMessage])
          console.error(errors)
        })
    },

    handleSuccess() {
      this.errorMsgs = []
      this.isLoading = false

      document.dispatchEvent(
        new CustomEvent('milesNBonusLoading', {
          detail: { loading: this.isLoading },
        })
      )
    },

    handleError(messages) {
      this.errorMsgs = messages
      this.isLoading = false

      document.dispatchEvent(
        new CustomEvent('milesNBonusLoading', {
          detail: { loading: this.isLoading },
        })
      )

      this.$nextTick(() => {
        lib.scrollToError(100)
      })
    },

    onClickChangeCard() {
      this.showCreditCardForm = true
    },
  },
}
</script>

<style scoped lang="scss">
#payment-iframe-frame {
  border: none;
  width: 100%;
  height: 450px;
  overflow: auto;
}
</style>
